import React, { useState } from "react";
import { ReactSortable } from "react-sortablejs";
import Sortable from 'sortablejs'
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { CheckCircle2, GripVertical } from "lucide-react";
import { moveSection, moveProject } from "@/lib/api";
import { updateProjectMonitoring } from "@/lib/api/project";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

type Project = {
  id: number;
  name: string;
  status?: string;
  activated?: boolean;
  hidden?: boolean;
  home?: boolean;
  [key: string]: any;
};

type Section = {
  id: number;
  title: string;
  projects?: Project[];
  [key: string]: any;
};

type SectionsAccordionProps = {
  siteSlug: string;
  sections: Section[];
  onNewProject: (sectionId: number) => void;
  openProjectFormSectionId: number | null;
  onNewSection?: () => void;
  onEditSection?: (section: Section) => void;
  onEditProject?: (project: Project, section: Section) => void;
};

function SectionSortableItem({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <div className="relative">
      <div
        className="section-drag-handle absolute left-0 top-3 z-10 cursor-grab"
        style={{ touchAction: "none" }}
        tabIndex={0}
        aria-label="Drag section"
      >
        <GripVertical className="w-4 h-4 text-gray-400" />
      </div>
      <div className="pl-6">{children}</div>
    </div>
  );
}

function ProjectSortableItem({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <div className="relative">
      <div
        className="project-drag-handle absolute left-0 top-3 z-10 cursor-grab"
        style={{ touchAction: "none" }}
        tabIndex={0}
        aria-label="Drag project"
      >
        <GripVertical className="w-4 h-4 text-gray-400" />
      </div>
      <div className="pl-6">{children}</div>
    </div>
  );
}

const SectionsAccordion: React.FC<SectionsAccordionProps> = ({
  siteSlug,
  sections,
  onNewProject,
  openProjectFormSectionId,
  onNewSection,
  onEditSection,
  onEditProject,
}) => {
  const [sectionList, setSectionList] = useState<Section[]>(sections);
  const [projectLists, setProjectLists] = useState<Record<number, Project[]>>(
    Object.fromEntries(sections.map(s => [s.id, s.projects || []]))
  );
  const [expandedSections, setExpandedSections] = useState<string[]>([]);

  const queryClient = useQueryClient();

  const updateMonitoringMutation = useMutation({
    mutationFn: async ({
      projectId,
      activated,
    }: {
      projectId: number;
      activated: boolean;
    }) => {
      // Find the siteSlug from props
      return updateProjectMonitoring(siteSlug, projectId, activated);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site"] });
      toast("Project visibility updated!");
    },
    onError: () => {
      toast("Error updating project visibility", { style: { background: "#fee2e2", color: "#991b1b" } });
    },
  });

  const moveSectionMutation = useMutation({
    mutationFn: async ({ sectionSlug, position }: { sectionSlug: string; position: number }) =>
      {
        return moveSection(siteSlug, sectionSlug, position)
      },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site"] });
      toast("Sections sorted!");
    },
  });

  const moveProjectMutation = useMutation({
    mutationFn: async ({
      sectionSlug,
      projectSlug,
      targetSectionSlug,
      position,
    }: {
      sectionSlug: string;
      projectSlug: string;
      targetSectionSlug: string;
      position: number;
    }) =>
      moveProject(siteSlug, sectionSlug, projectSlug, targetSectionSlug, position),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site"] });
      toast("Projects sorted!");
    },
  });

  React.useEffect(() => {
    setSectionList(sections);
    setProjectLists(Object.fromEntries(sections.map(s => [s.id, s.projects || []])));
  }, [sections]);

  // Section sort handler (local state only)
  const handleSectionSort = (newList: Section[]) => {
    setSectionList(newList);
  };

  // Project sort handler (local state only)
  const handleProjectSort = (sectionId: number, newList: Project[], allLists: Project[][]) => {
    setProjectLists(prev => ({ ...prev, [sectionId]: newList }));
  };

  // Section drop handler (update backend)
  const handleSectionDrop = (evt: any) => {
    // evt.newIndex is the new index of the moved section in the new order
    // evt.item is the moved DOM element, evt.to is the container
    if (evt.oldIndex === undefined || evt.newIndex === undefined || evt.oldIndex === evt.newIndex) return;
    const movedSection = sectionList[evt.oldIndex]
    if (!movedSection) return;
    const sectionSlug = movedSection.slug || movedSection.id.toString();
    moveSectionMutation.mutate({ sectionSlug, position: evt.newIndex+1});
  };

  // Project drop handler (update backend)
  const handleProjectDrop = (targetSectionId: number) => (evt: any) => {
    // evt.oldIndex: index in source section before move
    // evt.newIndex: index in target section after move
    // evt.from: source DOM element, evt.to: target DOM element
    // evt.clone: the moved DOM element (not the data)
    // evt.pullMode: "clone" or undefined

    // Find the moved project by comparing previous and new project lists
    const prevProjects = projectLists[targetSectionId] || [];
    const newProjects = evt.to && evt.to.sortable ? evt.to.sortable.toArray() : [];
    // Fallback: use evt.newIndex and projectLists[targetSectionId]
    let movedProject: Project | undefined;
    if (typeof evt.newIndex === "number") {
      movedProject = (projectLists[targetSectionId] || [])[evt.oldIndex];
    }
    if (!movedProject) return;

    // Find the previous section (the one that no longer contains the project)
    let sourceSectionId: number | undefined;
    for (const [secId, projects] of Object.entries(projectLists)) {
      if (Number(secId) !== targetSectionId && projects.find(p => p.id === movedProject!.id)) {
        sourceSectionId = Number(secId);
        break;
      }
    }
    // If not found, fallback to targetSectionId (reorder within same section)
    if (sourceSectionId === undefined) sourceSectionId = targetSectionId;

    // Use slugs if available, else fallback to id
    // const section = sections.find(s => s.id === targetSectionId);
    //const targetSectionSlug = section?.slug || targetSectionId.toString();
    //const sourceSection = sections.find(s => s.id === sourceSectionId);
    // const sourceSectionSlug = sourceSection?.slug || sourceSectionId.toString();
    const projectSlug = movedProject.slug || movedProject.id.toString();

    // Find new position (1-based) in the target section

    const targetSectionSlug = Sortable.get(evt.to).options.dataSlug
    const sourceSectionSlug = Sortable.get(evt.from).options.dataSlug

    const newPosition = evt.newIndex + 1
      // (projectLists[targetSectionId] || []).findIndex(p => p.id === movedProject.id) + 1;

    moveProjectMutation.mutate({
      sectionSlug: sourceSectionSlug,
      projectSlug,
      targetSectionSlug,
      position: newPosition,
    });
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button
          size="sm"
          variant="default"
          onClick={onNewSection}
        >
          + New Section
        </Button>
      </div>
      <Accordion
        type="multiple"
        className="w-full"
        value={expandedSections}
        onValueChange={setExpandedSections}
      >
        <ReactSortable
          list={sectionList}
          setList={handleSectionSort}
          animation={200}
          handle=".section-drag-handle"
          tag="div"
          onEnd={handleSectionDrop}
        >
        {sectionList.map(section => (
          <SectionSortableItem key={section.id}>
            <AccordionItem value={section.id.toString()} className="mb-2">
              <AccordionTrigger className="font-semibold text-lg">
                {section.title}
              </AccordionTrigger>
              <AccordionContent className="overflow-visible">
                <div className="flex justify-between items-center mb-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onEditSection && onEditSection(section)}
                  >
                    Edit section
                  </Button>
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => onNewProject(section.id)}
                  >
                    {openProjectFormSectionId === section.id ? "Cancel" : "+ New Project"}
                  </Button>
                </div>
                <ReactSortable
                  list={projectLists[section.id] || []}
                  setList={newList => handleProjectSort(section.id, newList, Object.values(projectLists))}
                  group="projects"
                  animation={200}
                  handle=".project-drag-handle"
                  tag="div"
                  dataSlug={section.slug}
                  className="space-y-2 overflow-visible"
                  onEnd={handleProjectDrop(section.id)}
                >
                  {(projectLists[section.id] || []).map(project => (
                    <ProjectSortableItem key={project.id}>
                      <Card className={`p-3 ${project.activated ? "bg-pink-200" : "bg-yellow-200"} overflow-visible`}>
                        <div className="flex justify-between items-center">
                          <div className="flex items-center">
                            <span className="font-semibold">{project.title}</span>
                            <Badge className="ml-2" variant={project.activated ? "default" : "secondary"}>
                              {project.activated ? "activated" : "hidden"}
                            </Badge>

                            {project.home && (
                              <Badge className="ml-2 bg-green-600 border-green-500 text-white" variant={"outline"}>
                                <CheckCircle2 className="mr-1 h-10" />
                                <span>Home</span>
                              </Badge>
                            )}
                          </div>
                          <div className="flex gap-2 items-center">
                            <Switch
                              checked={project.activated}
                              onCheckedChange={(checked) => {
                                updateMonitoringMutation.mutate({
                                  projectId: project.id,
                                  activated: checked,
                                });
                              }}
                              disabled={updateMonitoringMutation.isPending}
                              aria-label={project.activated ? "Set hidden" : "Set activated"}
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => onEditProject && onEditProject(project, section)}
                            >
                              Edit
                            </Button>
                            
                          </div>
                        </div>
                      </Card>
                    </ProjectSortableItem>
                  ))}
                </ReactSortable>
                {(!section.projects || section.projects.length === 0) && (
                  <div className="bg-yellow-200 p-2 rounded flex items-center justify-between">
                    <span>Projects don't found in section {section.title}.</span>
                    <Button size="sm" variant="ghost">×</Button>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          </SectionSortableItem>
        ))}
      </ReactSortable>
      </Accordion>
    </div>
  );
};

export type { Section, Project, SectionsAccordionProps };
export default SectionsAccordion;
